import { Injectable } from '@angular/core';
import { Action, createSelector, Selector, State, StateContext, Store } from '@ngxs/store';
import { AppService } from '@Core/services/proprietary/app.service';
import { StateHelperService } from '@Core/services/proprietary/state-helper.service';
import { cloneDeep } from 'src/ep-utils';
import {
    AddCorrespondOptions,
    AddDocument,
    AddFiling,
    AddFilingItem,
    RemoveAllFilingsForDocID,
    RemoveDocument,
    RemoveFiling,
    RemoveFilingItem,
    ResetState,
    SetCorrespondTemplates,
    UpdateCorrespondOptions,
    UpdateDocument,
    UpdateDocumentID,
    UpdateFiling
} from './document-distribute-object.actions';
import { DocumentDistributeObjectDefaults } from './document-distribute-object.defaults';

export interface DocumentDistributeObjectStateModel {
    entities: any;
    document: any;
    filing: any;
    correspondOptions: any;
    correspondTemplates: [];
}

const DocumentDistributeObjectStateDefaults: DocumentDistributeObjectStateModel = {
    entities: {},
    document: {},
    filing: {},
    correspondOptions: {},
    correspondTemplates: []
};

@State<DocumentDistributeObjectStateModel>({
    name: 'DocumentDistributeObjectState',
    defaults: DocumentDistributeObjectStateDefaults
})
@Injectable()
export class DocumentDistributeObjectState {
    constructor(private helper: StateHelperService, private app: AppService, private store: Store) { }

    //************** SELECTORS  ***************
    @Selector()
    static entities(state: DocumentDistributeObjectStateModel) {
        let tempEntities = { ...state.entities };
        tempEntities.document = state.document;
        tempEntities.filing = state.filing;
        tempEntities.correspondOptions = state.correspondOptions;
        return tempEntities;
    }

    @Selector()
    static documents(state: DocumentDistributeObjectStateModel) {
        let result: any = [];
        for (var i in state.document) {
            result.push(state.document[i]);
        }
        return result;
    }

    @Selector()
    static documentCount(state: DocumentDistributeObjectStateModel) {
        let count = 0;
        for (var i in state.document) {
            count++;
        }
        return count;
    }

    @Selector()
    static filings(state: DocumentDistributeObjectStateModel) {
        let result: any = [];
        for (var i in state.filing) {
            result.push(state.filing[i]);
        }
        return result;
    }

    @Selector()
    static filingCount(state: DocumentDistributeObjectStateModel) {
        let count = 0;
        for (var i in state.filing) {
            count++;
        }
        return count;
    }

    @Selector()
    static correspondOptions(state: DocumentDistributeObjectStateModel) {
        return state.correspondOptions;
    }

    @Selector()
    static correspondTemplates(state: DocumentDistributeObjectStateModel) {
        return state.correspondTemplates || [];
    }

    static document(docID: string) {
        return createSelector([DocumentDistributeObjectState], (state: DocumentDistributeObjectStateModel) => {
            return state.document[docID];
        });
    }

    static filingByDocID(docID: string) {
        return createSelector([DocumentDistributeObjectState], (state: DocumentDistributeObjectStateModel) => {
            let result: any = [];
            let document = state.document[docID];

            if (document) {
                let docFilings = document['Filing'];
                if (docFilings) {
                    for (var i in state.filing) {
                        if (docFilings.indexOf(state.filing[i].DocLink_ID) >= 0) {
                            result.push(state.filing[i]);
                        }
                    }
                }
            }

            return result;
        });
    }

    static distMethodByDocID(docID: string) {
        return createSelector([DocumentDistributeObjectState], (state: DocumentDistributeObjectStateModel) => {
            let result: string = '';
            let document = state.document[docID];

            if (document && document.Dist_Method && document.Dist_Method != '') {
                result = document.Dist_Method;
            }

            return result;
        });
    }

    //******  ACTIONS  ******
    @Action(ResetState)
    resetState(sc: StateContext<DocumentDistributeObjectStateModel>, action: ResetState) {
        sc.patchState({
            entities: {},
            document: {},
            filing: {},
            correspondOptions: {},
            correspondTemplates: []
        });
    }

    @Action(AddDocument)
    addDocument(sc: StateContext<DocumentDistributeObjectStateModel>, action: AddDocument) {
        const state = sc.getState();
        let newDocument = this.helper.createDefaultItem(DocumentDistributeObjectDefaults, 'Document', action.document);
        //let newDocument = action.document;
        let docID = newDocument.Doc_ID;

        if (docID) {
            //Use provided docID (May still be a placeholder ID)
        } else {
            docID = this.helper.getTempID();
            newDocument.Doc_ID = docID;
        }

        sc.patchState({
            document: { ...state.document, [docID]: newDocument }
        });
    }

    @Action(RemoveDocument)
    removeDocument(sc: StateContext<DocumentDistributeObjectStateModel>, action: RemoveDocument) {
        const state = sc.getState();
        let document = { ...state.document };
        delete document[action.docID];
        sc.patchState({ document: document });
    }

    @Action(AddFiling)
    addFiling(sc: StateContext<DocumentDistributeObjectStateModel>, action: AddFiling) {
        const state = sc.getState();
        let docID = action.docID;
        let newFiling = this.helper.createDefaultItem(DocumentDistributeObjectDefaults, 'Filing', action.filing);
        //let newFiling = action.filing;
        let distMethod = state.document[docID].Dist_Method;

        if (!newFiling.Book) {
            newFiling.Book = { Entity_ID: this.app.dsSystemInfo.objectNode.Context.Book.Entity_ID };
        }

        if (!newFiling.Book_Loc) {
            newFiling.Book_Loc = { Entity_ID: this.app.dsSystemInfo.objectNode.Context.Book_Loc.Entity_ID };
        }

        if (newFiling && newFiling.Distribute && newFiling.Distribute.Method && newFiling.Distribute.Method != '') {
            //Already has a distMethod specified
        } else {
            newFiling.Distribute = { Method: distMethod };
        }

        if (distMethod == 'F' || distMethod == 'P') {
            newFiling.File_Only_Ind = 'Y';
        }

        let docLinkID = this.helper.getTempID();
        newFiling.DocLink_ID = docLinkID;

        sc.patchState({
            filing: { ...state.filing, [docLinkID]: newFiling }
        });

        sc.dispatch([new AddFilingItem(docID, docLinkID)]);
    }

    @Action(RemoveFiling)
    removeFiling(sc: StateContext<DocumentDistributeObjectStateModel>, action: RemoveFiling) {
        const state = sc.getState();
        let filing = { ...state.filing };
        delete filing[action.docLinkID];
        sc.patchState({ filing: filing });

        sc.dispatch([new RemoveFilingItem(action.docID, action.docLinkID)]);
    }

    @Action(RemoveAllFilingsForDocID)
    removeAllFilingsForDocID(sc: StateContext<DocumentDistributeObjectStateModel>, action: RemoveAllFilingsForDocID) {
        const state = sc.getState();
        let filing = { ...state.filing };
        let docID = action.docID;

        let currentDocument = cloneDeep(state.document[docID]);
        if (currentDocument) {
            let docFilings = currentDocument['Filing'];
            if (docFilings) {
                for (var i in filing) {
                    if (docFilings.indexOf(filing[i].DocLink_ID) >= 0) {
                        currentDocument.Filing = this.helper.removeArrayItemByValue(currentDocument.Filing, filing[i].DocLink_ID);
                        delete filing[i];
                    }
                }
            }
        }

        sc.patchState({ filing: filing, document: { ...state.document, [action.docID]: currentDocument } });
    }

    @Action(UpdateDocument)
    updateDocument(sc: StateContext<DocumentDistributeObjectStateModel>, action: UpdateDocument) {
        const state = sc.getState();
        let updateDocument = { ...action.document };
        let docID = updateDocument.Doc_ID;
        let currentDocument = state.document[docID];
        let updatefiling = cloneDeep(state.filing);

        sc.patchState({
            document: {
                ...state.document,
                [docID]: {
                    ...state.document[docID],
                    ...updateDocument
                }
            }
        });

        if (updateDocument && currentDocument && updateDocument.Dist_Method != currentDocument.Dist_Method) {
            //Push Dist_Method to filings for this document.

            let document = state.document[docID];
            if (document) {
                let docFilings = document['Filing'];
                if (docFilings) {
                    for (var i in updatefiling) {
                        if (docFilings.indexOf(updatefiling[i].DocLink_ID) >= 0) {
                            updatefiling[i].Distribute = { Method: updateDocument.Dist_Method };

                            if (updateDocument.Dist_Method == 'F' || updateDocument.Dist_Method == 'P') {
                                updatefiling[i].File_Only_Ind = 'Y';
                            } else {
                                updatefiling[i].File_Only_Ind = 'N';
                            }
                        }
                    }
                }
            }
            sc.patchState({ filing: updatefiling });
        }
    }

    @Action(UpdateDocumentID)
    updateDocumentID(sc: StateContext<DocumentDistributeObjectStateModel>, action: UpdateDocumentID) {
        const state = sc.getState();
        if (action.docID != action.newDocID) {
            let newDocument = cloneDeep(state.document[action.docID]);

            newDocument.Doc_ID = action.newDocID;

            sc.patchState({
                document: {
                    [action.newDocID]: newDocument
                }
            });
        }
    }

    @Action(UpdateFiling)
    updateFiling(sc: StateContext<DocumentDistributeObjectStateModel>, action: UpdateFiling) {
        const state = sc.getState();
        let updateFiling = action.filing;
        let docLinkID = updateFiling.DocLink_ID;

        sc.patchState({
            filing: {
                ...state.filing,
                [docLinkID]: {
                    ...state.filing[docLinkID],
                    ...updateFiling
                }
            }
        });
    }

    @Action(AddFilingItem)
    addFilingItem(sc: StateContext<DocumentDistributeObjectStateModel>, action: AddFilingItem) {
        const state = sc.getState();
        let docID = action.docID;

        let document = state.document[docID] ? { ...state.document[docID] } : {};

        if (!document['Filing']) {
            document['Filing'] = [];
        }

        document['Filing'] = [...document['Filing'], action.docLinkID];

        sc.patchState({
            document: {
                ...state.document,
                [docID]: {
                    ...state.document[docID],
                    ...document
                }
            }
        });
    }

    @Action(RemoveFilingItem)
    removeFilingItem(sc: StateContext<DocumentDistributeObjectStateModel>, action: RemoveFilingItem) {
        const state = sc.getState();
        let docID = action.docID;

        let document = state.document[docID] ? { ...state.document[docID] } : {};
        document.Filing = this.helper.removeArrayItemByValue(document.Filing, action.docLinkID);

        sc.patchState({
            document: {
                ...state.document,
                [docID]: {
                    ...state.document[docID],
                    ...document
                }
            }
        });
    }

    @Action(AddCorrespondOptions)
    addCorrespondOptions(sc: StateContext<DocumentDistributeObjectStateModel>, action: AddCorrespondOptions) {
        const state = sc.getState();
        let updateCorrespondOptions = action.correspondOptions;

        sc.patchState({ correspondOptions: updateCorrespondOptions });
    }

    @Action(UpdateCorrespondOptions)
    updateCorrespondOptions(sc: StateContext<DocumentDistributeObjectStateModel>, action: UpdateCorrespondOptions) {
        const state = sc.getState();
        let updateCorrespondOptions = { ...action.correspondOptions };

        if (
            updateCorrespondOptions.Cover_Memo_Object_Code == '' &&
            state.correspondOptions.Cover_Memo_Object_Code &&
            state.correspondOptions.Cover_Memo_Object_Code != ''
        ) {
            updateCorrespondOptions.Subject = '';
            updateCorrespondOptions.Message = '';
        }

        if (updateCorrespondOptions.Cover_Memo_Object_Code != '') {
            updateCorrespondOptions.Subject = '<Defined By Template>';
            updateCorrespondOptions.Message = '<Defined By Template>';
        }

        sc.patchState({ correspondOptions: updateCorrespondOptions });
    }

    @Action(SetCorrespondTemplates)
    setCorrespondTemplates(sc: StateContext<DocumentDistributeObjectStateModel>, action: SetCorrespondTemplates) {
        sc.patchState({ correspondTemplates: action.correspondTemplates });
    }
}
